// src/pages/Company/ViewCompanies.jsx
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import companyService from '../../services/company.service';
import './ViewCompanies.css';

const ViewCompanies = () => {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Redirect if not Super Admin
  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/dashboard/overview');
    }
  }, [isSuperAdmin, navigate]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCompanies();
    }
  }, [isSuperAdmin]);

  const fetchCompanies = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await companyService.getAllCompanies();
      if (response.success && response.data) {
        // Handle direct array response
        const companiesData = Array.isArray(response.data) ? response.data : [];
        setCompanies(companiesData);
      } else {
        setCompanies([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch companies');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async (companyData) => {
    try {
      const response = await companyService.createCompany(companyData);
      
      if (response.success) {
        fetchCompanies();
        alert('Company added successfully!');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add company');
      throw err;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this company?')) {
      return;
    }

    try {
      const response = await companyService.deleteCompany(id);
      if (response.success) {
        fetchCompanies();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete company');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter companies based on search term
  const filteredCompanies = companies.filter(company => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      company.name?.toLowerCase().includes(searchLower) ||
      company.code?.toLowerCase().includes(searchLower) ||
      company.email?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="view-companies-container">
      {/* Header */}
      <div className="companies-header">
        <h4 className="companies-title">View Companies</h4>
        <button 
          className="btn-add-company-header" 
          onClick={() => navigate('/company/add')}
        >
          Add Company
        </button>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Search companies by name, code, email..."
          className="search-input"
          value={searchTerm}
          onChange={handleSearch}
        />
        <span className="search-icon">🔍</span>
      </div>

      {/* Companies Table */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading companies...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="btn-retry" onClick={fetchCompanies}>
            Retry
          </button>
        </div>
      ) : !Array.isArray(filteredCompanies) || filteredCompanies.length === 0 ? (
        <div className="empty-state">
          <p>{searchTerm ? 'No companies match your search' : 'No companies found'}</p>
          <button className="btn-add-company" onClick={() => navigate('/company/add')}>
            {searchTerm ? 'Clear Search' : 'Add Your First Company'}
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="companies-table">
            <thead>
              <tr>
                <th>COMPANY CODE</th>
                <th>COMPANY NAME</th>
                <th>EMAIL</th>
                <th>PHONE</th>
                <th>ADDED DATE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => (
                <tr key={company.id}>
                  <td>{company.code}</td>
                  <td>{company.name}</td>
                  <td>{company.email || '-'}</td>
                  <td>{company.phone || '-'}</td>
                  <td>{formatDate(company.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-action btn-view">View</button>
                      <button 
                        className="btn-action btn-delete"
                        onClick={() => handleDelete(company.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div className="table-footer">
            <p>Showing {filteredCompanies.length} of {companies.length} companies</p>
          </div>
        </div>
      )}


    </div>
  );
};

export default ViewCompanies;